import React, { useState, useEffect } from 'react';
import './settle-now.css';
import { getSupabase } from '../lib/supabase.js';
import {
} from '../lib/useInvestmentData.js';

/**
 * SettleNow — records a payment from the investor to Kareem against
 * outstanding kareem_share from value_events. Inserts a row in
 * investment_settlements; the next Home/AdminOverview load picks up
 * the new outstanding balance automatically.
 */

const PAYMENT_METHODS = [
  { id: 'mercury',  name: 'Mercury',         detail: 'ACH · Free · 1-2 biz days', color: '#0f172a', initials: 'MR' },
  { id: 'zelle',    name: 'Zelle',           detail: 'Bank-to-bank · Instant · Free', color: '#6d28d9', initials: 'Z' },
  { id: 'wire',     name: 'Wire',            detail: 'Same-day · $25 fee',         color: '#1d4ed8', initials: 'WR' },
  { id: 'coinbase', name: 'Coinbase (USDC)', detail: 'Instant · ~$0.50 gas',       color: '#1e40af', initials: 'CB' },
  { id: 'other',    name: 'Other',           detail: 'Note where it went',         color: '#f3f4fa', initials: '··' },
];

export default function SettleNow({ member, navigate }) {
  const [outstanding, setOutstanding] = useState(0);
  // Breakdown so the user sees how outstanding was computed. Contract path
  // (Release 5 / T-022): the D4 waterfall components straight from
  // v2_household_summary. Legacy path (RPC unreachable): the old split-share
  // fields, rendered by the old breakdown.
  const [breakdown, setBreakdown] = useState(null); // waterfall components when the contract answered
  const [grossKareemShare, setGrossKareemShare] = useState(0);
  const [costShare, setCostShare] = useState(0);
  const [accruedCost, setAccruedCost] = useState(0);
  const [settledTotal, setSettledTotal] = useState(0);
  const [splitPct, setSplitPct] = useState(0.5);
  const [hurdlePct, setHurdlePct] = useState(0);
  const [cyclesComplete, setCyclesComplete] = useState(0);
  const [loadingCtx, setLoadingCtx] = useState(true);
  const [ctxError, setCtxError] = useState(null);

  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState('mercury');
  const [memo, setMemo] = useState('');
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Contract-only (D-15 Session C): v2_household_summary is the single
  // source of the money figures here; if it cannot be read, the screen
  // shows the failure instead of computing anything locally.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb || !member?.id) { setLoadingCtx(false); return; }
      // Contract-first (Release 5 / T-022): one authoritative summary.
      try {
        const { data: s, error: sErr } = await sb.rpc('v2_household_summary', { p_member_id: member.id });
        if (!sErr && s && typeof s === 'object' && s.holder) {
          if (!cancelled) {
            const out = Math.round(Number(s.outstanding || 0));
            setOutstanding(out);
            setBreakdown({
              totalValue: Math.round(Number(s.totalValue || 0)),
              recognizedCosts: Math.round(Number(s.recognizedCosts || 0)),
              // D-13: only the investor-paid slice relieves what they owe.
              kareemPaidCosts: Math.round(Number(s.kareemPaidCosts || 0)),
              claimCosts: Math.round(Number(s.recognizedCosts || 0) - Number(s.kareemPaidCosts || 0)),
              // D-15: costs are LINES now; attestedCosts is a retired key.
              itemCosts: Math.round(Number(s.itemCosts || 0)),
              transactionFees: Math.round(Number(s.transactionFees || 0)),
              profitPool: Number(s.profitPool || 0),
              hurdleTarget: Number(s.hurdleTarget || 0),
              split: Number(s.split ?? 0.5),
              claim: Math.round(Number(s.claim || 0)),
              poolHeld: Math.round(Number(s.poolHeldValue || 0)),
              waivers: Math.round(Number(s.waivers || 0)),
              // D-9: Kareem's payouts render as their own "+ paid back" line.
              paidOut: Math.round(Number(s.paidToInvestor || 0)),
              credit: Math.round(Number(s.credit || 0)),
              costsProvisional: s.costsProvisional !== false,
            });
            // D-9: the "Payments confirmed" line is what YOU paid in — the
            // net (confirmedPayments) shrinks when Kareem pays out and would
            // misstate your own payments.
            setSettledTotal(Math.round(Number(s.paidByInvestor ?? s.confirmedPayments ?? 0)));
            setSplitPct(Number(s.split ?? 0.5));
            setAmount(out);
            const tgt = Number(s.hurdleTarget || 0);
            setHurdlePct(tgt > 0 ? Math.min(1, Number(s.hurdleProgress || 0) / tgt) : 0);
            const { count } = await sb.from('value_events')
              .select('id', { count: 'exact', head: true })
              .eq('enrolled_member_id', member.id)
              .is('voided_at', null);
            setCyclesComplete(count || 0);
            setLoadingCtx(false);
          }
          return;
        }
      } catch { /* fall through to the honest failure below */ }
      // D-15 Session C: the meter fallback is retired. The contract is the
      // only source of money figures — a failure shows a failure (T-034),
      // never a custody-blind number that looks right.
      if (!cancelled) {
        setCtxError('The summary service could not be reached — money figures cannot be shown. Pull to retry or reload.');
        setLoadingCtx(false);
      }
    })();
    return () => { cancelled = true; };
  }, [member?.id]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');
      if (!member?.id) throw new Error('No member context');
      const today = new Date().toISOString().slice(0, 10);
      const { error } = await sb.from('investment_settlements').insert({
        enrolled_member_id: member.id,
        amount,
        settle_date: today,
        payment_method: PAYMENT_METHODS.find((m) => m.id === method)?.name || method,
        memo: memo || null,
      });
      if (error) throw error;
      setSubmitted(true);
      // Tell other mounted screens (Home, AdminOverview) to re-fetch.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member.id, moduleName: 'investment' } }));
      }
      setTimeout(() => { navigate && navigate('home'); }, 900);
    } catch (e) {
      setSubmitError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMethod = PAYMENT_METHODS.find((m) => m.id === method);
  const hurdlePctLabel = (hurdlePct * 100).toFixed(1).replace(/\.0$/, '');

  if (loadingCtx) {
    return <main className="settle-page" style={{ color: '#64748b' }}>Loading outstanding balance…</main>;
  }

  return (
    <main className="settle-page">
      <header className="settle-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <nav className="settle-breadcrumb">
            <a href="#home">Investment</a> <span>›</span> <span>Settle now</span>
          </nav>
          <a className="btn-ghost" href="#home" style={{ flexShrink: 0 }}>← Back</a>
        </div>
        <h1 className="settle-title">Settle outstanding to Kareem</h1>
        <p className="settle-sub">
          Settlements pay down what you owe Kareem from value events. They don't
          affect hurdle progress — that math is set by value events alone.
        </p>
      </header>

      <section className="settle-hero">
        <div className="settle-hero-row">
          <span className="settle-hero-label">Outstanding · settles monthly</span>
        </div>
        <div className="settle-hero-amount">
          <span className="settle-hero-currency">$</span>
          <span className="settle-hero-num mono">{outstanding.toLocaleString()}</span>
        </div>
        <div className="settle-hero-meta">
          {cyclesComplete} {cyclesComplete === 1 ? 'event' : 'events'} · Hurdle {hurdlePctLabel}% · Settle anytime; cadence is a nudge, not a hard date
        </div>
      </section>

      {/* Show-the-calculation (Release 5 / T-022): the D4 waterfall from the
          summary contract. value − costs → pool → claim, then the D21/D18
          reductions. Rendered whenever the contract answered; the legacy
          split-share block below survives only for the RPC-unreachable
          fallback. */}
      {breakdown && (
        <section className="settle-card settle-breakdown">
          <div className="settle-breakdown-title">How this was calculated</div>
          <div className="settle-breakdown-row">
            <span className="sb-lbl">Value generated (gross)</span>
            <span className="sb-val mono">${breakdown.totalValue.toLocaleString()}</span>
          </div>
          {(breakdown.claimCosts ?? breakdown.recognizedCosts) > 0 && (
            <div className="settle-breakdown-row sb-negative">
              <span className="sb-lbl">
                − Costs you covered
                <span className="sb-sub">
                  confirmed cost lines ${breakdown.itemCosts.toLocaleString()}
                  {breakdown.transactionFees > 0 ? ` · transaction fees $${breakdown.transactionFees.toLocaleString()}` : ''}
                  {Number(breakdown.kareemPaidCosts || 0) > 0 ? ` · Kareem-paid costs ($${breakdown.kareemPaidCosts.toLocaleString()}) slow his clock, not your bill` : ''}
                </span>
              </span>
              <span className="sb-val mono">−${(breakdown.claimCosts ?? breakdown.recognizedCosts).toLocaleString()}</span>
            </div>
          )}
          <div className="settle-breakdown-row">
            <span className="sb-lbl">
              Kareem's claim on the profit pool
              <span className="sb-sub">
                {breakdown.profitPool < 0
                  ? `pool is −$${Math.abs(breakdown.profitPool).toLocaleString()} — nothing due yet, deficit carried forward`
                  : breakdown.hurdleTarget > 0
                    ? `pool fills the $${breakdown.hurdleTarget.toLocaleString()} recovery target first, then splits ${Math.round(breakdown.split * 100)}/${Math.round((1 - breakdown.split) * 100)}`
                    : `straight ${Math.round(breakdown.split * 100)}/${Math.round((1 - breakdown.split) * 100)} split from the first profitable dollar`}
              </span>
            </span>
            <span className="sb-val mono">${breakdown.claim.toLocaleString()}</span>
          </div>
          {breakdown.poolHeld > 0 && (
            <div className="settle-breakdown-row sb-negative">
              <span className="sb-lbl">− Value the TK-C pool already holds</span>
              <span className="sb-val mono">−${breakdown.poolHeld.toLocaleString()}</span>
            </div>
          )}
          {settledTotal > 0 && (
            <div className="settle-breakdown-row sb-negative">
              <span className="sb-lbl">− Payments confirmed</span>
              <span className="sb-val mono">−${settledTotal.toLocaleString()}</span>
            </div>
          )}
          {breakdown.waivers > 0 && (
            <div className="settle-breakdown-row sb-negative">
              <span className="sb-lbl">− Waived by Kareem</span>
              <span className="sb-val mono">−${breakdown.waivers.toLocaleString()}</span>
            </div>
          )}
          {(breakdown.paidOut || 0) > 0 && (
            <div className="settle-breakdown-row">
              <span className="sb-lbl">+ Paid back to you by Kareem</span>
              <span className="sb-val mono" style={{ color: 'var(--green, #059669)' }}>+${breakdown.paidOut.toLocaleString()}</span>
            </div>
          )}
          <div className="settle-breakdown-row settle-breakdown-total">
            <span className="sb-lbl">{breakdown.credit > 0 ? 'Kareem owes you' : 'Outstanding to Kareem'}</span>
            <span className="sb-val mono">
              {breakdown.credit > 0 ? `$${breakdown.credit.toLocaleString()}` : `$${outstanding.toLocaleString()}`}
            </span>
          </div>
          {breakdown.costsProvisional && (
            <div className="settle-breakdown-note">
              Cost history is still being confirmed month by month — recognized
              costs cover only confirmed facts, so this figure can move as
              actual costs are recorded.
            </div>
          )}
        </section>
      )}
      {!breakdown && (costShare > 0 || accruedCost > 0) && (
        <section className="settle-card settle-breakdown">
          <div className="settle-breakdown-title">How this was calculated</div>
          <div className="settle-breakdown-row">
            <span className="sb-lbl">Gross owed (your kareem share)</span>
            <span className="sb-val mono">${grossKareemShare.toLocaleString()}</span>
          </div>
          <div className="settle-breakdown-row sb-negative">
            <span className="sb-lbl">
              − Your share of operating costs
              <span className="sb-sub">
                {Math.round(splitPct * 100)}% × ${accruedCost.toLocaleString()} accrued
              </span>
            </span>
            <span className="sb-val mono">−${costShare.toLocaleString()}</span>
          </div>
          {settledTotal > 0 && (
            <div className="settle-breakdown-row sb-negative">
              <span className="sb-lbl">− Already settled</span>
              <span className="sb-val mono">−${settledTotal.toLocaleString()}</span>
            </div>
          )}
          <div className="settle-breakdown-row settle-breakdown-total">
            <span className="sb-lbl">Outstanding to Kareem</span>
            <span className="sb-val mono">${outstanding.toLocaleString()}</span>
          </div>
        </section>
      )}

      {ctxError && (
        <section className="settle-card" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#9f1239' }}>
          <strong>Couldn't load outstanding balance.</strong>
          <div className="mono" style={{ fontSize: 12, marginTop: 6 }}>{ctxError}</div>
        </section>
      )}

      <section className="settle-card">
        <div className="settle-field-row">
          <label className="settle-label">Amount</label>
          <div className="settle-quick-chips">
            <button onClick={() => setAmount(Math.round(outstanding * 0.5))} disabled={outstanding <= 0}>50%</button>
            <button onClick={() => setAmount(outstanding)} disabled={outstanding <= 0}>FULL</button>
          </div>
        </div>
        <div className="settle-amount-input">
          <span className="settle-amount-currency">$</span>
          <input
            onWheel={(e) => e.currentTarget.blur()}
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            className="mono"
          />
          <button className="settle-amount-max" onClick={() => setAmount(outstanding)} disabled={outstanding <= 0}>
            MAX <span>${outstanding}</span>
          </button>
        </div>
      </section>

      <section className="settle-card">
        <label className="settle-label">Payment method</label>
        <div className="settle-methods">
          {PAYMENT_METHODS.map((pm) => (
            <button
              key={pm.id}
              className={'settle-method' + (method === pm.id ? ' selected' : '')}
              onClick={() => setMethod(pm.id)}
            >
              <span className="settle-method-tile" style={{ background: pm.color, color: pm.id === 'other' ? '#475569' : '#fff' }}>
                {pm.initials}
              </span>
              <span className="settle-method-body">
                <span className="settle-method-name">{pm.name}</span>
                <span className="settle-method-detail">{pm.detail}</span>
              </span>
              <span className={'settle-method-radio' + (method === pm.id ? ' selected' : '')} />
            </button>
          ))}
        </div>
      </section>

      <section className="settle-card">
        <label className="settle-label">Memo <span className="settle-label-opt">· optional</span></label>
        <input
          type="text"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder='Optional note · e.g. "Cycle 4 settle, Mercury txn ABC123"'
          className="settle-memo-input"
        />
      </section>

      <section className="settle-after">
        <div className="settle-after-title">After this settlement</div>
        <div className="settle-after-row">
          <span>Outstanding:</span>
          <span className="mono">
            ${outstanding.toLocaleString()} →{' '}
            <strong style={{ color: '#059669' }}>
              ${Math.max(0, outstanding - amount).toLocaleString()}
            </strong>
          </span>
        </div>
        <div className="settle-after-row">
          <span>Hurdle:</span>
          <span className="mono">{hurdlePctLabel}% (UNCHANGED)</span>
        </div>
        <p className="settle-after-note">
          Hurdle progress is only affected by value generated, not settlements.
        </p>
      </section>

      <section className="settle-toggle-row">
        <label>
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Notify Kareem via Telegram
        </label>
      </section>

      {submitError && (
        <section className="settle-card" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#9f1239' }}>
          <strong>Save failed:</strong> {submitError}
        </section>
      )}

      <footer className="settle-footer">
        <button className="btn-ghost" onClick={() => navigate && navigate('home')} disabled={submitting}>Cancel</button>
        <button
          className="btn-primary"
          disabled={submitting || submitted || amount <= 0}
          onClick={handleSubmit}
        >
          {submitted ? 'Saved ✓' : submitting ? 'Saving…' : `Settle $${amount.toLocaleString()} via ${selectedMethod?.name}`}
        </button>
      </footer>
    </main>
  );
}
